import { useCallback, useEffect, useState } from 'react';
import { api, Turf } from './adminApi';
import { inputCls, btnPrimary, btnGhost, cardCls } from './AdminPanel';

export default function AdminTurfs({ onToast }: { onToast: (msg: string) => void }) {
  const [turfs, setTurfs] = useState<Turf[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Turf | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    const res = await api<{ turfs: Turf[] }>('/api/turfs');
    if (res.ok && res.data) setTurfs(res.data.turfs);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (turf: Turf) => {
    if (!window.confirm(`Delete turf "${turf.name}"?`)) return;
    const res = await api(`/api/turfs/${(turf as any).id}`, { method: 'DELETE' });
    if (res.ok) { onToast('Turf deleted'); await load(); } else onToast(res.error || 'Delete failed');
  };

  if (loading) return <p className="text-sm text-gray-500">Loading turfs…</p>;

  return (
    <div className={cardCls}>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-bold">Turf directory ({turfs.length})</h2>
        <div className="flex gap-2">
          <button onClick={load} className={btnGhost}>↻ Refresh</button>
          <button onClick={() => setCreating(true)} className={btnPrimary}>+ New turf</button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-white/10 text-xs uppercase tracking-wide text-gray-500">
              <th className="py-2 pr-3">Name</th><th className="py-2 pr-3">Area</th><th className="py-2 pr-3">Size</th><th className="py-2 pr-3">Rate/hr</th><th className="py-2 pr-3">Rating</th><th className="py-2 pr-3">Matches</th><th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {turfs.map((t) => (
              <tr key={(t as any).id} className="border-b border-white/5 hover:bg-white/5">
                <td className="py-2 pr-3"><b>{t.name}</b><div className="text-xs text-gray-500">{t.address}</div></td>
                <td className="py-2 pr-3">{t.area}</td>
                <td className="py-2 pr-3">{t.size}</td>
                <td className="py-2 pr-3">৳{t.pricePerHour ?? (t as any).price_per_hour ?? 0}</td>
                <td className="py-2 pr-3">{t.rating ?? '—'}</td>
                <td className="py-2 pr-3">{(t as any).totalMatchesPlayed ?? (t as any).total_matches ?? 0}</td>
                <td className="py-2"><div className="flex gap-2"><button onClick={() => setEditing(t)} className={btnGhost}>Edit</button><button onClick={() => remove(t)} className="rounded-lg border border-red-500/30 px-3 py-1.5 text-sm font-semibold text-red-400 hover:bg-red-500/10">Delete</button></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(creating || editing) && <TurfForm turf={editing} onClose={() => { setCreating(false); setEditing(null); }} onDone={async () => { setCreating(false); setEditing(null); await load(); }} onToast={onToast} />}
    </div>
  );
}

function TurfForm({ turf, onClose, onDone, onToast }: { turf: Turf | null; onClose: () => void; onDone: () => void; onToast: (m: string) => void }) {
  const isEdit = Boolean(turf);
  const [form, setForm] = useState(() => ({
    name: turf?.name || '',
    area: turf?.area || '',
    address: turf?.address || '',
    phone: turf?.phone || '',
    whatsapp: turf?.whatsapp || '',
    size: turf?.size || '7v7',
    pricePerHour: String((turf as any)?.pricePerHour ?? (turf as any)?.price_per_hour ?? 0),
    facilities: Array.isArray((turf as any)?.facilities) ? ((turf as any)?.facilities as string[]).join(', ') : '',
    isIndoor: Boolean((turf as any)?.isIndoor),
    imageUrl: turf?.imageUrl || (turf as any)?.image_url || '',
    rating: String(turf?.rating ?? 0),
    notes: turf?.notes || '',
  }));
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    const payload = {
      name: form.name, area: form.area, address: form.address, phone: form.phone, whatsapp: form.whatsapp,
      size: form.size, pricePerHour: Number(form.pricePerHour) || 0,
      facilities: form.facilities.split(',').map((s) => s.trim()).filter(Boolean),
      isIndoor: form.isIndoor, imageUrl: form.imageUrl, rating: Number(form.rating) || 0, notes: form.notes,
    };
    const res = api(`/api/turfs${isEdit ? `/${(turf as any).id}` : ''}`, { method: isEdit ? 'PATCH' : 'POST', body: JSON.stringify(payload) });
    const result = await res;
    setBusy(false);
    if (result.ok) { onToast(isEdit ? 'Turf updated' : 'Turf created'); onDone(); } else onToast(result.error || 'Save failed');
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#12181f] p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between"><h3 className="text-lg font-black">{isEdit ? 'Edit turf' : 'New turf'}</h3><button onClick={onClose} className="text-gray-500 hover:text-white">×</button></div>
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 block"><span className="text-xs text-gray-500">Name</span><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
          <label className="block"><span className="text-xs text-gray-500">Area</span><input className={inputCls} value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} /></label>
          <label className="block"><span className="text-xs text-gray-500">Address</span><input className={inputCls} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></label>
          <label className="block"><span className="text-xs text-gray-500">Phone</span><input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></label>
          <label className="block"><span className="text-xs text-gray-500">WhatsApp</span><input className={inputCls} value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} /></label>
          <label className="block"><span className="text-xs text-gray-500">Size</span>
            <select className={inputCls} value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })}>
              {['5v5', '6v6', '7v7', '8v8', '11v11'].map((s) => <option key={s}>{s}</option>)}
            </select>
          </label>
          <label className="block"><span className="text-xs text-gray-500">Rate per hour (৳)</span><input className={inputCls} type="number" value={form.pricePerHour} onChange={(e) => setForm({ ...form, pricePerHour: e.target.value })} /></label>
          <label className="block"><span className="text-xs text-gray-500">Facilities (comma separated)</span><input className={inputCls} value={form.facilities} onChange={(e) => setForm({ ...form, facilities: e.target.value })} /></label>
          <label className="block"><span className="text-xs text-gray-500">Rating</span><input className={inputCls} type="number" step="0.1" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} /></label>
          <label className="col-span-2 block"><span className="text-xs text-gray-500">Image URL</span><input className={inputCls} value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} /></label>
          <label className="col-span-2 block"><span className="text-xs text-gray-500">Notes</span><input className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.isIndoor} onChange={(e) => setForm({ ...form, isIndoor: e.target.checked })} /> Indoor turf</label>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} disabled={busy} onClick={submit}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}